/**
 * Enemy AI: each tick, one BFS from the player (shared by chaser and wanderer);
 * interceptor and anticipator additionally compute their own BFS from a
 * projected target tile or the player's likely flag.
 *
 * All four pickers are deterministic functions of their inputs. Wanderer uses
 * a stateless hash of (tick, enemyId) for "noise" so replays stay byte-identical
 * without threading state.rng through the AI.
 *
 * Tie-breaking everywhere is by direction order N/E/S/W (DIRECTIONS).
 */

import { type Maze, type Tile, isPassable } from './maze.ts';
import type { Direction } from './types.ts';
import type { FlagState } from './types.ts';
import { DIRECTIONS, dirVec, isReverse } from './direction.ts';

/** Distance from `from` to every passable tile, BFS. Boulders block. */
export function bfsDistanceMap(maze: Maze, from: { x: number; y: number }): Map<string, number> {
  const dist = new Map<string, number>();
  const fromKey = `${from.x},${from.y}`;
  if (!isPassable(maze, from.x, from.y)) return dist;
  dist.set(fromKey, 0);
  const queue: { x: number; y: number }[] = [from];
  while (queue.length > 0) {
    const t = queue.shift() as { x: number; y: number };
    const td = dist.get(`${t.x},${t.y}`) as number;
    for (const d of DIRECTIONS) {
      const v = dirVec(d);
      const nx = t.x + v.dx;
      const ny = t.y + v.dy;
      const key = `${nx},${ny}`;
      if (dist.has(key)) continue;
      if (!isPassable(maze, nx, ny)) continue;
      dist.set(key, td + 1);
      queue.push({ x: nx, y: ny });
    }
  }
  return dist;
}

/**
 * Rank the legal neighbor directions for an enemy at `from`, ascending by
 * distance-to-target via `distMap`, tie-broken by DIRECTIONS order.
 *
 * Reverse is excluded if `currentDir` is set; if that yields an empty list
 * (dead end), the function re-runs allowing reverse so the enemy never freezes.
 */
function rankedNeighbors(
  maze: Maze,
  from: { x: number; y: number },
  currentDir: Direction | null,
  distMap: Map<string, number>,
): Direction[] {
  function collect(allowReverse: boolean): { dir: Direction; dist: number }[] {
    const out: { dir: Direction; dist: number }[] = [];
    for (const d of DIRECTIONS) {
      if (!allowReverse && currentDir !== null && isReverse(d, currentDir)) continue;
      const v = dirVec(d);
      const nx = from.x + v.dx;
      const ny = from.y + v.dy;
      if (!isPassable(maze, nx, ny)) continue;
      const dist = distMap.get(`${nx},${ny}`);
      if (dist === undefined) continue;
      out.push({ dir: d, dist });
    }
    return out;
  }
  let cands = collect(false);
  if (cands.length === 0) cands = collect(true);
  // Stable sort by distance asc; DIRECTIONS-order is preserved on ties.
  cands.sort((a, b) => a.dist - b.dist);
  return cands.map((c) => c.dir);
}

/**
 * Pick the direction the chaser enemy should head from `from`. Returns null
 * if no legal move exists (isolated tile — shouldn't happen on generated mazes).
 *
 * Algorithm:
 *  1. Rank non-U-turn neighbors by ascending distance-to-player; tie-break N/E/S/W.
 *  2. If no non-U-turn candidate exists, allow the U-turn.
 *  3. Return the first ranked direction.
 */
export function pickEnemyDirection(
  maze: Maze,
  from: { x: number; y: number },
  currentDir: Direction | null,
  distToPlayer: Map<string, number>,
): Direction | null {
  const ranked = rankedNeighbors(maze, from, currentDir, distToPlayer);
  return ranked[0] ?? null;
}

/**
 * Interceptor: BFS from a tile `lookahead` steps ahead of the player along
 * their current heading (clamped at the first wall). Falls back to chaser
 * when the player is stationary.
 */
export function pickInterceptorDirection(
  maze: Maze,
  from: { x: number; y: number },
  currentDir: Direction | null,
  playerTile: Tile,
  playerDir: Direction | null,
  lookahead: number,
  playerDistMap: Map<string, number>,
): Direction | null {
  if (playerDir === null) {
    return pickEnemyDirection(maze, from, currentDir, playerDistMap);
  }
  const target = projectPlayerTile(maze, playerTile, playerDir, lookahead);
  const distMap = bfsDistanceMap(maze, target);
  return pickEnemyDirection(maze, from, currentDir, distMap);
}

/**
 * Anticipator: find the uncollected flag with the smallest BFS-distance to the
 * player, then BFS from that flag and pick the neighbor closest to it. Falls
 * back to chaser if no uncollected flags exist or the chosen flag is unreachable
 * from the enemy.
 */
export function pickAnticipatorDirection(
  maze: Maze,
  from: { x: number; y: number },
  currentDir: Direction | null,
  flags: readonly FlagState[],
  playerDistMap: Map<string, number>,
): Direction | null {
  let best: Tile | null = null;
  let bestDist = Infinity;
  for (const f of flags) {
    if (f.collected) continue;
    const d = playerDistMap.get(`${f.tile.x},${f.tile.y}`);
    if (d === undefined) continue;
    if (d < bestDist) {
      bestDist = d;
      best = f.tile;
      continue;
    }
    // Tie-break: row-major (lower y wins, then lower x). Stable across ticks
    // so anticipator doesn't oscillate between equidistant flags.
    if (d === bestDist && best !== null) {
      if (f.tile.y < best.y || (f.tile.y === best.y && f.tile.x < best.x)) {
        best = f.tile;
      }
    }
  }
  if (best === null) {
    return pickEnemyDirection(maze, from, currentDir, playerDistMap);
  }
  const distMap = bfsDistanceMap(maze, best);
  return pickEnemyDirection(maze, from, currentDir, distMap);
}

/**
 * Wanderer: like chaser but with probability P picks the 2nd-best ranked
 * candidate instead of the best. P shrinks with stage so higher levels are
 * less noisy. Determinism comes from a stateless hash of (tick, enemyId).
 */
export function pickWandererDirection(
  maze: Maze,
  from: { x: number; y: number },
  currentDir: Direction | null,
  distToPlayer: Map<string, number>,
  tick: number,
  enemyId: number,
  stage: number,
): Direction | null {
  const ranked = rankedNeighbors(maze, from, currentDir, distToPlayer);
  if (ranked.length === 0) return null;
  if (ranked.length === 1) return ranked[0] ?? null;
  const p = Math.max(0.1, 0.3 - stage * 0.02);
  const swap = decisionNoise(tick, enemyId) < p;
  return (swap ? ranked[1] : ranked[0]) ?? null;
}

/**
 * Walk from `playerTile` in `dir` up to `k` tiles; stop at the first wall.
 * Returns the final passable tile reached.
 */
export function projectPlayerTile(
  maze: Maze,
  playerTile: Tile,
  dir: Direction,
  k: number,
): Tile {
  const v = dirVec(dir);
  let x = playerTile.x;
  let y = playerTile.y;
  for (let i = 0; i < k; i++) {
    const nx = x + v.dx;
    const ny = y + v.dy;
    if (!isPassable(maze, nx, ny)) break;
    x = nx;
    y = ny;
  }
  return { x, y };
}

// AIDEV-NOTE: stateless deterministic hash for the wanderer. Splitmix-style
// 32-bit bit-mixing of (tick, enemyId) → [0, 1). Math.imul keeps each
// multiply within true 32-bit semantics; plain `*` would silently lose bits
// because the products exceed 2^53. Used instead of threading state.rng
// through the AI so replays stay byte-identical without an RNG side-channel.
export function decisionNoise(tick: number, enemyId: number): number {
  let x = (Math.imul(tick | 0, 0x1f3517a3) + Math.imul(enemyId | 0, 0x9e3779b9)) | 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b) | 0;
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35) | 0;
  x = x ^ (x >>> 16);
  return (x >>> 0) / 0x100000000;
}
